/* eslint-disable import/first */
import React, { useState, useEffect, useRef, useCallback } from 'react'
import useScroll from 'hooks/useScroll'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { Link as ExternalLink } from 'components/misc/Link'
import { Fade } from 'components/misc/Fade'
import useClickOut from 'hooks/useClickOut'
import SearchBox from 'components/search/SearchBox'
import NavButtons from 'components/navbar/NavButtons'
import Measure from 'components/misc/Measure'
import { Slide } from 'components/misc/Slide'
import { navSubmit } from 'store/search/actions'
import { completeOnboardingStep, setSearchFocused, setSearchTerm, toggleMobileSearch } from 'store/ui/actions'
import HoverLink from 'components/navbar/HoverLink'
import NavCategoryDropdown from 'components/navbar/NavCategoryDropdown'
import { setTab, TABS } from 'store/home/actions'
import styles from './styles/NavBar.css'
import classNames from 'classnames'
import Banner from '../banner/Banner'
import Header from '../typography/Header'
import { TopBanner } from '../top-banner/TopBanner'
import SubscriptionBanner from './SubscriptionBar'

const getFeatureItems = user => [
  { label: 'Assistant', to: '/assistant', desc: 'AI grounded in research' },
  { label: 'Search', to: '/search', desc: 'Full-text citation search' },
  {
    label: 'Collections',
    to: user?.email ? '/collections' : '/features#collections',
    desc: 'Organize and monitor papers'
  }
]

const INTEGRATIONS_ITEMS = [
  { label: 'MCP', to: '/mcp', desc: 'Connect AI agents to Scite' },
  { label: 'API', to: '/api', desc: 'Programmatic data access' },
  { label: 'Holdings', to: '/integrations#holdings', desc: 'Full-text via your library' },
  {
    label: 'Zotero Plugin',
    href: 'https://github.com/scitedotai/scite-zotero-plugin',
    desc: 'Citation data in Zotero'
  },
  { label: 'Browser Extension', to: '/extension-install', desc: 'Citation badges anywhere' }
]

const AUTH_PATHS = ['/log-in', '/sign-up']

const SEARCH_PLACEHOLDER = 'Search by title, author, keywords or DOI'

//
// TODO:
//       Use of this hard coded breakpoint is kind of a hack...
//
const LARGE_BREAKPOINT = 60 * 16
const MEDIUM_BREAKPOINT = 30 * 16

const MobileSearch = () => {
  const dispatch = useDispatch()
  const input = useRef(null)
  const [mounted, setMounted] = useState(false)
  const showMobileSearch = useSelector(({ ui }) => ui.showMobileSearch)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (mounted && input.current && showMobileSearch) {
      input.current.focus()
    }
  }, [showMobileSearch])

  return (
    <Fade className={styles.mobileSearch} show={showMobileSearch} data-testid='mobile-search-box'>
      <SearchBox
        className={styles.mobileSearchBox}
        inputClass={styles.mobileSearchInput}
        dropdownClass={styles.mobileDropdownClass}
        placeholder={SEARCH_PLACEHOLDER}
        onSubmit={e => {
          dispatch(navSubmit())
          dispatch(toggleMobileSearch())
        }}
        inputRef={input}
        dropdown
      />
      {}
      <i
        className={styles.mobileSearchCloseIcon}
        role='button'
        tabIndex='0'
        aria-label='Close search'
        onClick={_ => dispatch(toggleMobileSearch())}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') dispatch(toggleMobileSearch())
        }}
      />
    </Fade>
  )
}

function NavBarBody({
  className,
  staticPage = false,
  homePage = false,
  sticky = false,
  spacer = false,
  assistant = false,
  setChatHistoryOpen,
  chatHistoryOpen,
  searchBarBorder = true,
  onMobileMenuClick,
  hiddenForSidebar = false
}) {
  const ref = useRef()
  const location = useLocation()
  const hideLogo = AUTH_PATHS.includes(location.pathname)
  const dispatch = useDispatch()
  const hasUser = useSelector(({ ui }) => ui.hasUser)
  const user = useSelector(({ ui }) => ui.user)
  const extensionLoaded = useSelector(({ ui }) => ui.extensionLoaded)
  const focused = useSelector(({ ui }) => ui.search.focused)
  const [large, setLarge] = useState(true)
  const [medium, setMedium] = useState(false)
  const [show, setShow] = useState(true)
  const [openCategory, setOpenCategory] = useState(null)

  const setCategoryOpen = (category, open) => {
    setOpenCategory(current => (open ? category : current === category ? null : current))
  }

  useEffect(() => {
    const updateBreakpoints = () => {
      setLarge(window.innerWidth >= LARGE_BREAKPOINT)
      setMedium(window.innerWidth >= MEDIUM_BREAKPOINT)
    }
    updateBreakpoints()
    window.addEventListener('resize', updateBreakpoints)
    return () => window.removeEventListener('resize', updateBreakpoints)
  }, [])

  const handleScroll = useCallback(
    ({ lastKnownY }) => {
      const currentY = window.scrollY

      if (currentY > lastKnownY && currentY > 50) {
        setShow(false)
      } else {
        setShow(true)
      }
    },
    [setShow]
  )

  useScroll(handleScroll)

  useClickOut(ref, focused, () => {
    if (spacer) {
      return
    }

    dispatch(setSearchFocused(false))
  })

  useEffect(() => {
    if (user && user.onboarding && !user.onboarding.extension?.initialOnboarding && extensionLoaded) {
      dispatch(
        completeOnboardingStep({
          category: 'extension',
          step: 'initial_onboarding',
          email: user.email
        })
      )
    }
  }, [user, extensionLoaded])

  return (
    <nav
      ref={ref}
      className={classNames(styles.navBarContainer, {
        [styles.sticky]: sticky,
        [styles.spacer]: spacer,
        [styles.hiddenForSidebar]: hiddenForSidebar
      })}
      /* For tests where there is no CSS */
      style={spacer ? { visibility: 'hidden' } : {}}
    >
      <Slide
        className={classNames(styles.navBar, {
          [className]: className,
          [styles.transparent]: staticPage,
          [styles.notStatic]: !staticPage,
          [styles.spacer]: spacer
        })}
        show={large || show || focused || spacer || (sticky && medium)}
      >
        <div className={styles.navContents}>
          {!assistant &&
            (hideLogo ? (
              <div className={styles.logo} />
            ) : (
              <Link
                onClick={() => {
                  dispatch(setTab(TABS.assistant))
                  dispatch(setSearchTerm(''))
                }}
                className={styles.logo}
                to={hasUser ? '/home' : '/'}
              >
                <img
                  alt='Blue scite logo'
                  width='105px'
                  height='28px'
                  className={styles.logoDesktop}
                  style={{ filter: 'contrast(2.15)' }}
                  src={`${CONFIG.assetsBaseURL}/images/logo-blue.svg`}
                />
                <img
                  alt='Blue scite logo'
                  width='64px'
                  height='38px'
                  className={styles.logoMobile}
                  style={{ filter: 'contrast(2.15)' }}
                  src={`${CONFIG.assetsBaseURL}/images/logo-blue.svg`}
                />
              </Link>
            ))}
          {assistant && (
            <div className={styles.assistantNavIdentifier}>
              <div
                role='button'
                tabIndex={0}
                className={styles.assistantHamburgerMenu}
                aria-label='Toggle chat history'
                onClick={() => setChatHistoryOpen(!chatHistoryOpen)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') setChatHistoryOpen(!chatHistoryOpen)
                }}
              >
                <i className={styles.assistantHamburgerMenuIcon} />
              </div>
              <ExternalLink to='/assistant' className={styles.assistantLink}>
                <Header className={styles.assistantNavHeader} level={6}>
                  assistant
                </Header>
              </ExternalLink>
              <span className={styles.by}>by</span>
              <ExternalLink to='/home' className={styles.assistantLogoLink}>
                <Header className={styles.assistantNavHeader} level={6}>
                  scite
                </Header>
              </ExternalLink>
            </div>
          )}
          {/* Hide nav when logged in on sidebar pages (nav moves to sidebar) */}
          {!assistant &&
            !(
              user?.email &&
              (location.pathname.startsWith('/assistant') ||
                location.pathname.startsWith('/search') ||
                (location.pathname.startsWith('/users/') &&
                  (location.pathname.startsWith('/users/me') || location.pathname.startsWith(`/users/${user?.slug}`))))
            ) && (
              <div className={styles.centerNav}>
                <NavCategoryDropdown
                  title='Features'
                  overviewTo='/features'
                  items={getFeatureItems(user)}
                  open={openCategory === 'features'}
                  setOpen={open => setCategoryOpen('features', open)}
                />
                <NavCategoryDropdown
                  title='Integrations'
                  overviewTo='/integrations'
                  items={INTEGRATIONS_ITEMS}
                  open={openCategory === 'integrations'}
                  setOpen={open => setCategoryOpen('integrations', open)}
                />
                <HoverLink to='/data' active={location.pathname === '/data'}>
                  Data
                </HoverLink>
                <HoverLink to='/ai-reads' active={location.pathname === '/ai-reads'}>
                  Rankings
                </HoverLink>
                <HoverLink to='/pricing' active={location.pathname === '/pricing'}>
                  Pricing
                </HoverLink>
              </div>
            )}
          <div className={styles.rightContent}>
            <div className={styles.navButtonsLayout}>
              <NavButtons
                buttonColor='blue'
                staticPage={staticPage || !user?.email}
                assistant={assistant}
                avatarBg={staticPage ? 'white' : 'gray'}
                onMobileMenuClick={onMobileMenuClick}
              />
            </div>
          </div>
        </div>

        {location.pathname !== '/search' && <MobileSearch />}
      </Slide>
    </nav>
  )
}

export const NavBar = props => {
  const user = useSelector(({ ui }) => ui.user)

  return (
    <div className={classNames('site-navbar', { [styles.hiddenForSidebar]: props.hiddenForSidebar })}>
      <Banner />
      <NavBarBody {...props} />
      <Measure name='nav'>
        <NavBarBody spacer hiddenForSidebar={props.hiddenForSidebar} />
      </Measure>
      {user && <SubscriptionBanner />}
      <TopBanner />
    </div>
  )
}

export default NavBar
